/**
 * Bond Mentor — API Client (Front End)
 *
 * Connects to the proxy server running on port 3001.
 * Falls back gracefully if the proxy is not available (Demo Mode).
 * Phase 36C: /api/health is intentionally fast and local-only so slow external
 * market providers cannot trigger startup timeout warnings.
 */

const PROXY_URL = (import.meta.env.VITE_PUBLIC_DATA_ENDPOINT || '').replace(/\/$/, '');

function apiUrl(endpoint) {
  // Prefer the Vite dev proxy when no public endpoint is configured.
  // This keeps browser calls same-origin in development and avoids localhost/127.0.0.1 mismatch noise.
  if (!PROXY_URL) return endpoint;
  return `${PROXY_URL}${endpoint}`;
}

/**
 * Generic fetch wrapper with timeout and error handling.
 */
async function apiFetch(endpoint, timeoutMs = 8000) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(apiUrl(endpoint), {
      signal: controller.signal,
      credentials: 'include',
    });
    clearTimeout(timeout);

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (err) {
    clearTimeout(timeout);
    if (err.name === 'AbortError') {
      console.warn(`API timeout: ${endpoint}`);
      return null;
    }
    console.warn(`API fetch failed (${endpoint}):`, err.message);
    return null;
  }
}


async function apiPost(endpoint, body = {}, timeoutMs = 8000) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(apiUrl(endpoint), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      signal: controller.signal,
      body: JSON.stringify(body),
    });
    clearTimeout(timeout);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || `API error: ${response.status} ${response.statusText}`);
    }
    return data;
  } catch (err) {
    clearTimeout(timeout);
    if (err.name === 'AbortError') {
      console.warn(`API timeout: ${endpoint}`);
      return { ok: false, message: 'Request timed out. Please try again.' };
    }
    console.warn(`API post failed (${endpoint}):`, err.message);
    return { ok: false, message: err.message || 'Request failed.' };
  }
}


async function apiPostQuiet(endpoint, body = {}, timeoutMs = 8000) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(apiUrl(endpoint), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      signal: controller.signal,
      body: JSON.stringify(body),
    });
    clearTimeout(timeout);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) return { ok: false, quiet: true, status: response.status, message: data?.message || `API error: ${response.status} ${response.statusText}` };
    return data;
  } catch (err) {
    clearTimeout(timeout);
    return { ok: false, quiet: true, message: err?.message || 'Request failed.' };
  }
}

export async function fetchAuthStatus() {
  return await apiFetch('/api/auth/status', 3000);
}

export async function login(email, password) {
  return await apiPost('/api/auth/login', { email, password }, 5000);
}

export async function logout() {
  return await apiPost('/api/auth/logout', {}, 3000);
}

export async function fetchCurrentUser() {
  return await apiFetch('/api/auth/me', 3000);
}

// DROP G — Account-level preferences
export async function fetchUserPreferences() {
  return await apiFetch('/api/auth/preferences', 4000);
}

export async function saveUserPreferences(preferences) {
  return await apiPostQuiet('/api/auth/preferences', { preferences }, 5000);
}

// DROP K — member profile
export async function fetchUserProfile() {
  return await apiFetch('/api/auth/profile', 5000);
}

export async function saveUserProfile(profile) {
  return await apiPost('/api/auth/profile', { profile }, 5000);
}

// DROP K Pass 2 — birthday reward
export async function fetchBirthdayStatus() {
  return await apiFetch('/api/auth/birthday', 4000);
}

export async function claimBirthdayReward() {
  return await apiPost('/api/auth/birthday/claim', {}, 5000);
}

/**
 * Check if the proxy server is running and healthy.
 */
export async function checkHealth() {
  return await apiFetch('/api/health', 1500);
}

/**
 * Fetch the full computed signal with all raw data.
 */
export async function fetchSignal() {
  return await apiFetch('/api/signal');
}

/**
 * Fetch fast dashboard snapshot. The old /api/all mega endpoint has been split into feed groups.
 */
function buildClientSideLiveFallback(reason = 'The /api/snapshot endpoint returned an error before JSON could be read.') {
  const now = new Date().toISOString();
  return {
    signal: {
      signal: 'YELLOW',
      compositeScore: 35,
      actionLevel: 'TIGHTEN_STOPS',
      subSignals: {
        yieldCurve: 50,
        creditSpreads: 50,
        fedPosture: 50,
        vigilanteActivity: 25,
        volatility: 35,
        liquidity: 35,
        moneyFlows: 25,
        globalBonds: 25,
      },
      computedAt: now,
      fallback: true,
      fallbackReason: reason,
    },
    yieldCurve: null,
    creditSpreads: null,
    fed: null,
    auctions: null,
    sectors: null,
    mode: 'LIVE',
    truthState: 'NO_LIVE_SIGNAL',
    statusMessage: `${reason} This is a safe fallback so the dashboard and Recent Signal History can still update while the server error is fixed.`,
    sources: {},
    errors: [{ source: 'api/snapshot', message: reason }],
    missingRequired: ['yieldCurve', 'creditSpreads', 'fed'],
    signalError: reason,
    fetchedAt: now,
  };
}

export async function fetchAllData() {
  // Fast split-feed snapshot. Optional slow feeds no longer block the dashboard.
  const data = await apiFetch('/api/snapshot', 18000);
  return data || buildClientSideLiveFallback('Live proxy is not reachable yet. Start the backend with npm run server, or run npm run dev:all to launch both the Vite app and Node proxy together.');
}

export async function fetchAdminHealth() {
  return await apiFetch('/api/admin/health', 12000);
}

export async function fetchOptionalFeeds() {
  return await apiFetch('/api/optional', 12000);
}


export async function fetchBacktestingReport() {
  return await apiFetch('/api/backtesting?limit=180', 12000);
}

export async function fetchProbabilityReport() {
  return await apiFetch('/api/probability?limit=180', 12000);
}


/**
 * Phase 41B — Admin Reset / Recheck Live
 *
 * Owner-only helper used by Admin Health. It clears server-side cache, confirms
 * the fast local health endpoint, checks Admin Health again, and asks for a new
 * snapshot so the owner can verify the app is back to current live/backup mode.
 */
export async function resetAndRecheckLive() {
  const startedAt = new Date().toISOString();

  const cache = await apiFetch('/api/admin/reset-live', 3000) || await apiFetch('/api/cache/clear', 3000);
  const health = await apiFetch('/api/health', 2000);
  const adminHealth = await apiFetch('/api/admin/health', 12000);
  const snapshot = await apiFetch('/api/snapshot', 18000);

  const ok = Boolean(
    health?.status === 'ok'
    && adminHealth?.server?.status
    && snapshot?.mode === 'LIVE'
  );

  return {
    ok,
    startedAt,
    finishedAt: new Date().toISOString(),
    cache,
    health,
    adminHealth,
    snapshot,
    message: ok
      ? 'Live checks refreshed. Cache was cleared, health responded, Admin Health responded, and a current dashboard snapshot was requested.'
      : 'Recheck finished, but one or more live checks did not return a complete response. Confirm node server/index.js is running and review Admin Health details.',
  };
}

/**
 * Fetch individual endpoints.
 */
export async function fetchYieldCurve() {
  return await apiFetch('/api/yield-curve');
}

export async function fetchCreditSpreads() {
  return await apiFetch('/api/credit-spreads');
}

export async function fetchFedData() {
  return await apiFetch('/api/fed');
}

export async function fetchAuctions() {
  return await apiFetch('/api/auctions');
}

export async function fetchSectors() {
  return await apiFetch('/api/sectors');
}

export async function fetchVIX() {
  return await apiFetch('/api/vix');
}

// ============================================
// Phase 43B — Invite-Only Registration
// ============================================
export async function fetchInvites() {
  return await apiFetch('/api/admin/invites', 5000);
}

export async function createInvite(payload) {
  return await apiPost('/api/admin/invites', payload, 5000);
}

export async function revokeInvite(inviteId) {
  return await apiPost(`/api/admin/invites/${encodeURIComponent(inviteId)}/revoke`, {}, 5000);
}

export async function updateInvite(inviteId, payload) {
  return await apiPost(`/api/admin/invites/${encodeURIComponent(inviteId)}`, payload, 5000);
}

// DROP L Pass 1 — billing config & offers
export async function fetchPublicBilling() {
  return await apiFetch('/api/billing/public', 5000);
}

export async function fetchAdminBilling() {
  return await apiFetch('/api/admin/billing', 5000);
}

export async function saveBillingTiers(tiers) {
  return await apiPost('/api/admin/billing/tiers', { tiers }, 5000);
}

export async function setBillingEnforce(enabled) {
  return await apiPost('/api/admin/billing/enforce', { enabled }, 5000);
}

export async function createBillingOffer(payload) {
  return await apiPost('/api/admin/billing/offers', payload, 5000);
}

export async function updateBillingOffer(id, payload) {
  return await apiPost(`/api/admin/billing/offers/${encodeURIComponent(id)}`, payload, 5000);
}

export async function deleteBillingOffer(id) {
  return await apiPost(`/api/admin/billing/offers/${encodeURIComponent(id)}/delete`, {}, 5000);
}

// DROP L Pass 2 — checkout & customer portal
export async function startCheckout({ tier, interval, offerId, donation } = {}) {
  return await apiPost('/api/billing/checkout', { tier, interval, offerId, donation }, 12000);
}

export async function openBillingPortal() {
  return await apiPost('/api/billing/portal', {}, 12000);
}

// DROP N — charity & giving
export async function fetchPublicCharity() {
  return await apiFetch('/api/charity/public', 5000);
}

export async function fetchAdminCharity() {
  return await apiFetch('/api/admin/charity', 5000);
}

export async function saveCharityConfig(config) {
  return await apiPost('/api/admin/charity/config', config, 5000);
}

export async function createCharity(payload) {
  return await apiPost('/api/admin/charity', payload, 5000);
}

export async function updateCharity(id, payload) {
  return await apiPost(`/api/admin/charity/${encodeURIComponent(id)}`, payload, 5000);
}

export async function deleteCharity(id) {
  return await apiPost(`/api/admin/charity/${encodeURIComponent(id)}/delete`, {}, 5000);
}

// DROP P — banners
export async function fetchMyBanners() {
  return await apiFetch('/api/banners', 5000);
}

export async function fetchAdminBanners() {
  return await apiFetch('/api/admin/banners', 5000);
}

export async function createBanner(payload) {
  return await apiPost('/api/admin/banners', payload, 5000);
}

export async function updateBanner(id, payload) {
  return await apiPost(`/api/admin/banners/${encodeURIComponent(id)}`, payload, 5000);
}

export async function deleteBanner(id) {
  return await apiPost(`/api/admin/banners/${encodeURIComponent(id)}/delete`, {}, 5000);
}

// ROLE CAPABILITIES — nav visibility
export async function fetchNavVisibility() {
  return await apiFetch('/api/nav/visibility', 5000);
}

export async function fetchAdminNavVisibility() {
  return await apiFetch('/api/admin/nav-visibility', 5000);
}

export async function saveNavVisibility(visibility) {
  return await apiPost('/api/admin/nav-visibility', { visibility }, 5000);
}

export async function restoreNavVisibility() {
  return await apiPost('/api/admin/nav-visibility/restore', {}, 5000);
}

// Treasury Auction Watch (graded)
export async function fetchAuctionWatch() {
  return await apiFetch('/api/auction-watch', 8000);
}

// Commodities (inflation tell)
export async function fetchCommodities() {
  return await apiFetch('/api/commodities', 9000);
}

// SEC / EDGAR filings (corporate-stress lens)
export async function fetchSecFilings() {
  return await apiFetch('/api/sec-filings', 9000);
}

// Macro Calendar Engine (admin)
export async function fetchMacroCalendarAdmin() {
  return await apiFetch('/api/admin/macro-calendar', 6000);
}
export async function fetchMacroCrosscheck() {
  return await apiFetch('/api/admin/macro-calendar/crosscheck', 8000);
}
export async function syncMacroCalendar() {
  return await apiPost('/api/admin/macro-calendar/sync', {}, 15000);
}
export async function saveMacroEvent(id, patch) {
  return await apiPost('/api/admin/macro-calendar/event', { id, patch }, 6000);
}
export async function hideMacroEvent(id, hidden) {
  return await apiPost(`/api/admin/macro-calendar/event/${encodeURIComponent(id)}/hide`, { hidden }, 6000);
}
export async function acceptMacroFeedDate(id) {
  return await apiPost(`/api/admin/macro-calendar/event/${encodeURIComponent(id)}/accept-feed`, {}, 6000);
}
export async function deleteMacroEvent(id) {
  return await apiPost(`/api/admin/macro-calendar/event/${encodeURIComponent(id)}/delete`, {}, 6000);
}
export async function restoreMacroCalendar() {
  return await apiPost('/api/admin/macro-calendar/restore', {}, 6000);
}
export async function saveMacroProviders(order, enabled) {
  return await apiPost('/api/admin/macro-calendar/providers', { order, enabled }, 6000);
}
export async function resetMacroProviders() {
  return await apiPost('/api/admin/macro-calendar/providers/reset', {}, 6000);
}

export async function lookupInvite(token) {
  return await apiFetch(`/api/invites/lookup/${encodeURIComponent(token)}`, 5000);
}

export async function registerWithInvite(payload) {
  return await apiPost('/api/invites/register', payload, 7000);
}

// ============================================
// Phase 43C — Waitlist Vetting
// ============================================
export async function submitWaitlistApplication(payload) {
  return await apiPost('/api/waitlist', payload, 7000);
}

export async function fetchWaitlistApplications() {
  return await apiFetch('/api/admin/waitlist', 7000);
}

export async function updateWaitlistApplicationStatus(applicationId, payload) {
  return await apiPost(`/api/admin/waitlist/${encodeURIComponent(applicationId)}/status`, payload, 5000);
}

export async function sendInviteFromWaitlist(applicationId, payload = {}) {
  return await apiPost(`/api/admin/waitlist/${encodeURIComponent(applicationId)}/send-invite`, payload, 7000);
}

// ============================================
// Phase 43D — Site Mode: Live / Waitlist / Maintenance
// ============================================
export async function fetchSiteMode() {
  return await apiFetch('/api/site-mode', 3000);
}

export async function saveSiteMode(payload) {
  return await apiPost('/api/admin/site-mode', payload, 5000);
}

// ============================================
// Phase 43E — Feedback Widget + Admin Feedback Inbox
// ============================================
export async function submitFeedback(payload) {
  return await apiPost('/api/feedback', payload, 5000);
}

// DROP J — admin user management
export async function fetchAdminUsers() {
  return await apiFetch('/api/admin/users', 5000);
}

export async function setUserRole(userId, role) {
  return await apiPost(`/api/admin/users/${userId}/role`, { role }, 5000);
}

export async function setUserComp(userId, type) {
  return await apiPost(`/api/admin/users/${userId}/comp`, { type }, 5000);
}

// DROP J Pass 2 — messaging & presence
export async function sendHeartbeat(status = 'active') {
  return await apiPostQuiet('/api/messages/heartbeat', { status }, 4000);
}

export async function fetchMyThread() {
  return await apiFetch('/api/messages/thread', 5000);
}

export async function sendMyMessage(body) {
  return await apiPost('/api/messages/thread', { body }, 5000);
}

export async function fetchAdminThreads() {
  return await apiFetch('/api/admin/messages/threads', 5000);
}

export async function fetchAdminThread(userId) {
  return await apiFetch(`/api/admin/messages/thread/${userId}`, 5000);
}

export async function sendAdminReply(userId, body) {
  return await apiPost(`/api/admin/messages/thread/${userId}`, { body }, 5000);
}

export async function fetchMessagingConfig() {
  return await apiFetch('/api/admin/messages/config', 4000);
}

export async function setMessagingConfig(enabled) {
  return await apiPost('/api/admin/messages/config', { enabled }, 5000);
}

export async function fetchFeedbackInbox() {
  return await apiFetch('/api/admin/feedback', 7000);
}

export async function updateFeedbackItem(feedbackId, payload) {
  return await apiPost(`/api/admin/feedback/${encodeURIComponent(feedbackId)}`, payload, 5000);
}

// ============================================
// Phase 43G — Admin Traffic Analytics & User Engagement
// ============================================
export async function trackAnalyticsEvent(payload) {
  // Analytics should never interrupt the dashboard. If the backend is offline, fail quietly.
  return await apiPostQuiet('/api/analytics/track', payload, 2500);
}

export async function fetchAnalyticsHealth() {
  return await apiFetch('/api/admin/analytics/health', 5000);
}

export async function fetchAdminAnalytics(period = '24h') {
  return await apiFetch(`/api/admin/analytics?period=${encodeURIComponent(period)}`, 7000);
}

export async function banAnalyticsUser(payload) {
  return await apiPost('/api/admin/analytics/ban', payload, 5000);
}

export async function clearAdminAnalytics(confirm) {
  return await apiPost('/api/admin/analytics/clear', { confirm }, 5000);
}

// ============================================
// Phase 43H — Backup, Restore & Migration Center
// ============================================
export async function fetchBackupHealth() {
  return await apiFetch('/api/admin/backups/health', 7000);
}

export async function fetchBackups() {
  return await apiFetch('/api/admin/backups', 7000);
}

export async function createBackupRequest(payload) {
  return await apiPost('/api/admin/backups/create', payload, 120000);
}

export async function previewBackupRestore(payload) {
  return await apiPost('/api/admin/backups/restore-preview', payload, 120000);
}

export async function restoreBackupRequest(payload) {
  return await apiPost('/api/admin/backups/restore', payload, 120000);
}

export async function deleteBackupRequest(fileName, confirm) {
  return await apiPost(`/api/admin/backups/${encodeURIComponent(fileName)}/delete`, { confirm }, 10000);
}
